import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  clearHistoryAiAssistantSession,
  getHistoryMessage,
  postChatAssistant,
  setAiAssistantSession
} from '@utils/api/ai-assistant';

import { addUserAiAssistantSessions } from '@redux/actions';
import { getAIAssistantCategory } from '@utils/get-ai-assistant-category';
import { createAIAdditionalContext } from '@utils/create-ai-additional-context';
import {
  currentChallengeIdSelector,
  userSelector
} from '../../../redux/selectors';
import {
  addChatHistoryMessage,
  sendMessageToAiError,
  setChatAssistantSessionId,
  setChatAssistantTyping,
  setChatHistoryMessages
} from './actions';
import { challengeMetaSelector, currentChallengeSelector } from './selectors';

function* sendMessageToAiAssistant({ payload }) {
  yield put(setChatAssistantTyping(true));

  const currentChallengeId = yield select(currentChallengeIdSelector);

  const challenge = yield select(currentChallengeSelector);

  const user = yield select(userSelector);

  const aiAssistantSession = user.aiAssistantSessions.find(
    session => session.exerciseId === currentChallengeId
  );

  const sessionId = aiAssistantSession ? aiAssistantSession.sessionId : null;

  try {
    const { content } = payload;

    yield put(
      addChatHistoryMessage({
        type: 'human',
        content
      })
    );

    const { superBlock: challengeModule } = challenge;

    console.log('challenge', challenge);
    const additionalContext = yield call(createAIAdditionalContext, challenge);

    const assistantCategory = yield call(
      getAIAssistantCategory,
      challengeModule
    );
    const response = yield call(postChatAssistant, {
      input: content,
      sessionId,
      assistantCategory,
      additionalContext
    });

    if (response.detail) {
      throw new Error(response.detail);
    }

    if (response) {
      const { content, session_id: generatedSessionId } = response;

      // If session not exists or different, set new session
      if (generatedSessionId !== sessionId) {
        yield call(setAiAssistantSession, {
          sessionId: generatedSessionId,
          exerciseId: currentChallengeId
        });

        yield put(
          addUserAiAssistantSessions({
            sessionId: generatedSessionId,
            exerciseId: currentChallengeId
          })
        );
      }

      yield put(
        setChatAssistantSessionId({ generatedSessionId, currentChallengeId })
      );

      yield put(addChatHistoryMessage({ content, type: 'ai' }));
    }
    yield put(sendMessageToAiError(false));
  } catch (error) {
    yield put(sendMessageToAiError(true));
  } finally {
    yield put(setChatAssistantTyping(false));
  }
}

function* getHistoryMessages({ payload: currentChallengeId }) {
  yield put(setChatAssistantTyping(true));

  const user = yield select(userSelector);

  const aiAssistantSession = user.aiAssistantSessions.find(
    session => session.exerciseId === currentChallengeId
  );

  const sessionId = aiAssistantSession ? aiAssistantSession.sessionId : null;

  const challenge = yield select(challengeMetaSelector);

  try {
    if (sessionId) {
      const { superBlock: challengeModule } = challenge;
      const assistantCategory = yield call(
        getAIAssistantCategory,
        challengeModule
      );

      const { history } = yield call(getHistoryMessage, {
        sessionId,
        assistantCategory
      });

      if (history) {
        yield put(setChatHistoryMessages(history));
      }
    } else {
      // Initialize empty history
      yield put(setChatHistoryMessages([]));
    }
    yield put(sendMessageToAiError(false));
  } catch (error) {
    yield put(sendMessageToAiError(true));
  } finally {
    yield put(setChatAssistantTyping(false));
  }
}

function* clearHistory() {
  const currentChallengeId = yield select(currentChallengeIdSelector);

  const user = yield select(userSelector);
  const aiAssistantSession = user.aiAssistantSessions.find(
    session => session.exerciseId === currentChallengeId
  );
  const challenge = yield select(challengeMetaSelector);

  try {
    const sessionId = aiAssistantSession ? aiAssistantSession.sessionId : null;

    if (!sessionId) {
      // should create a flash message warning user that there is no session and should contact support
      return;
    }

    const { superBlock: challengeModule } = challenge;
    const assistantCategory = yield call(
      getAIAssistantCategory,
      challengeModule
    );

    yield call(clearHistoryAiAssistantSession, {
      sessionId,
      assistantCategory
    });
    yield put(setChatHistoryMessages([]));
  } catch (error) {
    console.error('clearHistoryMessages', error);
  }
}

export function createAiAssistantSaga(types) {
  return [
    takeLatest(types.sendMessageToAi, sendMessageToAiAssistant),
    takeLatest(types.challengeMounted, getHistoryMessages),
    takeLatest(types.fetchClearHistoryMessages, clearHistory)
  ];
}
